import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, ShoppingBag } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV } from "@/config/site";
import { useCart } from "@/context/CartContext";
import { Wordmark } from "./Wordmark";
import { PreviewBanner } from "./PreviewBanner";
import { AnnouncementBar } from "./AnnouncementBar";
import { ShopNotice } from "@/components/agenda/AgendaNote";
import { getSource } from "@/lib/catalog";
import { patternBg } from "@/lib/patternBg";
import { Instagram, MessageCircle, X } from "lucide-react";
import { SITE, whatsappUrl } from "@/config/site";

const linkCls = (light) => ({ isActive }) => `bb-link py-1 text-[13.5px] tracking-[0.01em] transition-colors duration-500 ${light ? (isActive ? "font-semibold text-white" : "text-white/90 hover:text-white") : isActive ? "text-bordeaux" : "text-ink hover:text-bordeaux"}`;
// Páginas que abrem com uma fotografia a toda a largura: a barra fica transparente por cima dela, com letras brancas.
const HERO_ROUTES = ["/", "/sobre"];

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const overHero = HERO_ROUTES.includes(pathname);
  // No topo a barra é transparente (a página passa por baixo); ao rolar ganha um vidro leve e uma sombra suave.
  const [scrolled, setScrolled] = useState(false);
  // A barra encolhe-se (esconde-se) ao descer na página e volta assim que se sobe, para não ocupar ecrã sem necessidade.
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    const on = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (Math.abs(y - last) > 6) {
        setHidden(y > 140 && y > last);
        last = y;
      }
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  // Os elementos "colados" por baixo da barra (filtros, pré-visualização) sobem quando ela se esconde.
  useEffect(() => {
    document.documentElement.style.setProperty("--hdr", hidden ? "0px" : "60px");
  }, [hidden]);
  // Logótipo e letras sempre brancas. Transparente sobre o hero; em todos os outros casos (a rolar, ou em páginas sem fotografia) ganha um vidro escuro translúcido para as letras se lerem.
  // Sempre transparente, com o logótipo e os ícones a branco: uma sombra suave por trás garante a leitura em qualquer fundo.
  const light = true;
  return (
    <>
      {getSource() === "preview" && <PreviewBanner />}
      <AnnouncementBar />
      <ShopNotice />
      <header className={`sticky top-0 z-40 transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"} text-white [text-shadow:0_1px_12px_rgba(30,22,20,.55)] [&_svg]:drop-shadow-[0_1px_6px_rgba(30,22,20,.5)] ${overHero ? "-mb-[60px]" : ""}`}>
        <span aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-[110px] bg-gradient-to-b transition-opacity duration-500 ${scrolled || !overHero ? "from-[rgba(43,34,32,.5)] via-[rgba(43,34,32,.2)]" : "from-[rgba(43,34,32,.38)] via-[rgba(43,34,32,.12)]"} to-transparent`} />
        <div className="bb-container relative flex h-[60px] items-center justify-between gap-4">
          <button type="button" data-testid="nav-mobile-menu-button" className="-ml-2 grid h-11 w-11 place-items-center rounded-full lg:hidden" onClick={() => setMenu(true)} aria-label="Abrir menu">
            <Menu className="h-5 w-5" />
          </button>
          <Wordmark light={light} className="lg:mr-auto" />
          <nav aria-label="Principal" className="hidden items-center gap-9 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testId} className={linkCls(light)}>{n.label}</NavLink>
            ))}
          </nav>
          <button type="button" data-testid="nav-cart-button" onClick={() => setOpen(true)} className="relative -mr-2 flex h-11 items-center gap-1.5 rounded-full px-3 transition-colors hover:bg-current/10 lg:ml-8" aria-label={`Carrinho, ${count} artigos`}>
            <ShoppingBag className="h-[19px] w-[19px]" />
            {count > 0 && (
              <span data-testid="nav-cart-badge" className="grid h-5 min-w-5 place-items-center rounded-full bg-bordeaux px-1.5 text-[11px] font-semibold text-white">{count}</span>
            )}
          </button>
        </div>
      </header>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="flex w-[86vw] max-w-sm flex-col gap-0 border-0 bg-ivory p-0 [&>button]:hidden" data-testid="mobile-menu">
          <div className="relative flex h-[68px] shrink-0 items-center justify-between bg-[#D9BDB6] px-5" style={patternBg("vichy-rosa-claro", "217,189,182", 0.8, 240)}>
            <SheetTitle className="font-[Pacifico] text-[1.55rem] font-normal leading-none text-ink">Babybib</SheetTitle>
            <SheetClose className="grid h-11 w-11 place-items-center rounded-full text-ink hover:bg-ivory/50" aria-label="Fechar menu"><X className="h-5 w-5" /></SheetClose>
            <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-[2px] bg-[repeating-linear-gradient(90deg,rgba(117,67,75,.42)_0_7px,transparent_7px_13px)]" />
          </div>
          <SheetDescription className="sr-only">Navegação principal</SheetDescription>
          <nav className="flex flex-1 flex-col overflow-y-auto px-6 pt-4" aria-label="Menu móvel">
            {[{ to: "/", label: "Início", testId: "mobile-nav-home" }, ...NAV.map((n) => ({ ...n, testId: `mobile-${n.testId}` }))].map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={n.testId} onClick={() => setMenu(false)} className={({ isActive }) => `flex min-h-[56px] items-center justify-between border-b border-ink/10 font-display text-[1.375rem] ${isActive ? "text-bordeaux" : "text-ink"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col text-[15px] text-taupe-strong">
              {[["/perguntas-frequentes", "Perguntas frequentes"], ["/contactos", "Contactos"]].map(([to, label]) => (
                <NavLink key={to} to={to} onClick={() => setMenu(false)} className="flex min-h-[44px] items-center hover:text-bordeaux">{label}</NavLink>
              ))}
            </div>
          </nav>
          <div className="shrink-0 border-t-2 border-dashed border-bordeaux/25 px-6 py-5">
            <p className="mb-3 text-[13px] text-taupe-strong">Também recebo encomendas por mensagem 🤍</p>
            <div className="flex flex-wrap gap-2">
              {whatsappUrl() && <a href={whatsappUrl("Olá Alexandra! Gostava de saber mais 😊")} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-ink/20 px-4 text-sm text-ink"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
              <a href={SITE.instagram.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-ink/20 px-4 text-sm text-ink"><Instagram className="h-4 w-4" /> {SITE.instagram.handle}</a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
